import { useMemo, useState, useEffect } from 'react'
import { useMaybeRoomContext } from '@/src/context'
import type { Participant, RemoteParticipant } from 'livekit-client'
import { RoomEvent } from 'livekit-client'

interface Group {
  id: string
  name: string
}

export const useGroups = () => {
  const roomContext = useMaybeRoomContext()
  const [attributesUpdateTrigger, setAttributesUpdateTrigger] = useState(0)

  const groups = useMemo(() => {
    if (!roomContext?.metadata) return []

    try {
      const metadata = JSON.parse(roomContext.metadata)
      const groupsArray = Array.isArray(metadata.groups) ? (metadata.groups as Group[]) : []
      return groupsArray
    } catch (e) {
      console.error('Error parsing groups from metadata:', e)
      return []
    }
  }, [roomContext?.metadata])

  // Слушаем изменения атрибутов участников и метаданных комнаты
  useEffect(() => {
    if (!roomContext) return

    const handleRoomUpdate = () => {
      // Триггерим обновление при изменении атрибутов или метаданных
      setAttributesUpdateTrigger((prev) => prev + 1)
    }

    roomContext.on(RoomEvent.ParticipantAttributesChanged, handleRoomUpdate)
    roomContext.on(RoomEvent.RoomMetadataChanged, handleRoomUpdate)

    return () => {
      roomContext.off(RoomEvent.ParticipantAttributesChanged, handleRoomUpdate)
      roomContext.off(RoomEvent.RoomMetadataChanged, handleRoomUpdate)
    }
  }, [roomContext])

  const getParticipantsByGroup = useMemo(() => {
    const participantsByGroup = new Map<string, (Participant | RemoteParticipant)[]>()

    if (!roomContext) return participantsByGroup

    // Получаем mapping групп и список групп из metadata
    let participantGroupMap: Record<string, string | null> = {}
    let hasGroups = false

    try {
      if (roomContext.metadata) {
        const metadata = JSON.parse(roomContext.metadata)
        participantGroupMap = metadata.participantGroupMap || {}
        const groupsArray = Array.isArray(metadata.groups) ? metadata.groups : []
        hasGroups = groupsArray.length > 0
      }
    } catch (e) {
      console.error('Error parsing participantGroupMap from metadata:', e)
    }

    // Если групп нет (группы завершены или ещё не созданы),
    // считаем, что никто не состоит в группах
    if (!hasGroups) {
      return participantsByGroup
    }

    const addParticipantToGroup = (participant: Participant | RemoteParticipant) => {
      // Сначала проверяем metadata, затем атрибуты
      let groupId: string | null | undefined

      if (participant.identity in participantGroupMap) {
        groupId = participantGroupMap[participant.identity]
      } else {
        groupId = participant.attributes?.groupId
      }

      // Пропускаем участников без группы
      if (!groupId || typeof groupId !== 'string') return

      if (!participantsByGroup.has(groupId)) {
        participantsByGroup.set(groupId, [])
      }
      participantsByGroup.get(groupId)!.push(participant)
    }

    // Добавляем локального участника, если он в группе
    if (roomContext.localParticipant) {
      addParticipantToGroup(roomContext.localParticipant)
    }

    // Добавляем удаленных участников
    roomContext.remoteParticipants.forEach((participant) => {
      addParticipantToGroup(participant)
    })

    return participantsByGroup
  }, [
    roomContext,
    roomContext?.metadata,
    roomContext?.localParticipant?.attributes?.groupId,
    roomContext?.remoteParticipants.size,
    attributesUpdateTrigger,
  ])

  const getCurrentUserGroupId = useMemo(() => {
    if (!roomContext?.localParticipant) return null

    // Проверяем metadata, затем атрибуты
    try {
      if (roomContext.metadata) {
        const metadata = JSON.parse(roomContext.metadata)
        const participantGroupMap = metadata.participantGroupMap || {}
        const groupsArray = Array.isArray(metadata.groups) ? metadata.groups : []

        // Если групп нет, локальный участник считается в общем зале
        if (groupsArray.length === 0) {
          return null
        }

        if (roomContext.localParticipant.identity in participantGroupMap) {
          const groupId = participantGroupMap[roomContext.localParticipant.identity]
          return groupId && typeof groupId === 'string' ? groupId : null
        }
      }
    } catch (e) {
      console.error('Error parsing getCurrentUserGroupId from metadata:', e)
    }

    const groupId = roomContext.localParticipant.attributes?.groupId
    return groupId && typeof groupId === 'string' ? groupId : null
  }, [roomContext?.localParticipant, roomContext?.metadata, attributesUpdateTrigger])

  return {
    groups,
    getParticipantsByGroup,
    getCurrentUserGroupId,
  }
}

